import React, { useEffect, useMemo, useState } from 'react';
import { ApiDrawer, getDrawers, addDrawer, updateDrawer, deleteDrawer, getUserGroups, ApiUserGroup, getDrawerPermissionsByDrawer, ApiPermission, addPermission, deletePermission } from '../api';
import { ApiPermissionTemplate, getPermissionTemplates, createPermissionTemplate } from '@api/permissionTemplates';
import { getFieldModels, ApiFieldModel } from '../api';

interface DrawerForm {
  drawerName: string;
  drawerDescription: string;
  drawerOrder: number;
  folderFieldModelId: number | null;
  documentFieldModelId: number | null;
  /** 0 = none, 1 = coding */
  metadataOption: 0 | 1;
  drawerPages: string; // comma-separated order
}
const emptyDrawer: DrawerForm = { drawerName: '', drawerDescription: '', drawerOrder: 0, folderFieldModelId: null, documentFieldModelId: null, metadataOption: 0, drawerPages: '' };
const OBJECTTYPE_DRAWER = 1; // backend enum

interface PermState { [groupId: number]: { read: boolean; write: boolean; modify: boolean; delete: boolean; full: boolean; templateId?: number; permissionId?: number; }; }

export default function DrawerAdmin() {
  const [drawers, setDrawers] = useState<ApiDrawer[]>([]);
  const [groups, setGroups] = useState<ApiUserGroup[]>([]);
  const [templates, setTemplates] = useState<ApiPermissionTemplate[]>([]);
  const [fieldModels, setFieldModels] = useState<ApiFieldModel[]>([]);
  const [selectedDrawerId, setSelectedDrawerId] = useState<number | null>(null);
  const [drawerEditing, setDrawerEditing] = useState(false);
  const [drawerForm, setDrawerForm] = useState<DrawerForm>(emptyDrawer);
  const [drawerFormOriginal, setDrawerFormOriginal] = useState<DrawerForm>(emptyDrawer);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permState, setPermState] = useState<PermState>({});
  const [permStateOriginal, setPermStateOriginal] = useState<PermState>({});
  const [pagesFlashKey, setPagesFlashKey] = useState(0);

  useEffect(() => { init(); }, []);

  // Sort groups alphabetically by name for consistent UI rendering
  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => a.groupName.localeCompare(b.groupName, undefined, { sensitivity: 'base' }));
  }, [groups]);

  const sortedDrawers = useMemo(() => {
    const getOrder = (d: ApiDrawer) => {
      const raw = (d as any)?.drawerOrder;
      if (raw === null || raw === undefined) return Number.MAX_SAFE_INTEGER;
      const n = Number(raw);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
    };

    return [...drawers].sort((a, b) => {
      const ao = getOrder(a);
      const bo = getOrder(b);
      if (ao !== bo) return ao - bo;
      return (a.drawerName ?? '').localeCompare((b.drawerName ?? ''), undefined, { sensitivity: 'base' });
    });
  }, [drawers]);

  async function init() {
    setLoading(true); setError(null);
    try { const [d, g, t, fm] = await Promise.all([getDrawers(), getUserGroups(), getPermissionTemplates(), getFieldModels()]); setDrawers(d); setGroups(g); setTemplates(t); setFieldModels(fm); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function loadPermissions(drawerId: number) {
    try {
      const perms = await getDrawerPermissionsByDrawer(drawerId); // assume returns list for drawer
      const map: PermState = {};
      perms.forEach(p => {
        const tmpl = templates.find(t => Number(t.id) === Number(p.permissionTemplateId));
        const gid = Number(p.groupId);
        map[gid] = {
          read: !!(tmpl && (tmpl.canRead ?? (tmpl as any).CanRead)),
          write: !!(tmpl && (tmpl.canWrite ?? (tmpl as any).CanWrite)),
          modify: !!(tmpl && (tmpl.canModify ?? (tmpl as any).CanModify)),
          delete: !!(tmpl && (tmpl.canDelete ?? (tmpl as any).CanDelete)),
          full: !!(tmpl && (tmpl.fullControl ?? (tmpl as any).FullControl)),
          templateId: Number(p.permissionTemplateId),
          permissionId: Number(p.id)
        };
      });
      // ensure every group present
      groups.forEach(g => { const gid = Number(g.id); if (!map[gid]) map[gid] = { read: false, write: false, modify: false, delete: false, full: false }; });
      setPermState(map);
      setPermStateOriginal(map);
    } catch { setPermState({}); }
  }

  function handleSelectDrawer(id: number) {
    setSelectedDrawerId(id);
    setDrawerEditing(true); // always go to edit mode
    const d = drawers.find(dr => Number(dr.id) === Number(id));
    const nextForm: DrawerForm = {
      drawerName: d?.drawerName || '',
      drawerDescription: d?.drawerDescription || '',
      drawerOrder: Number((d as any)?.drawerOrder ?? 0),
      folderFieldModelId: Number((d as any)?.drawerFolderFieldModelId ?? null),
      documentFieldModelId: Number((d as any)?.drawerDocumentFieldModelId ?? null),
      metadataOption: (Number((d as any)?.metadataOption ?? 0) === 1 ? 1 : 0),
      drawerPages: String((d as any)?.drawerPages ?? '')
    };
    setDrawerForm(nextForm);
    setDrawerFormOriginal(nextForm);
    loadPermissions(id);
  }
  function handleAddDrawer() {
    const maxExisting = drawers
      .map(d => Number((d as any)?.drawerOrder ?? 0))
      .filter(n => Number.isFinite(n) && n > 0)
      .reduce((max, n) => Math.max(max, n), 0);

    setSelectedDrawerId(null);
    setDrawerEditing(true);
    const next = { ...emptyDrawer, drawerOrder: maxExisting + 1 };
    setDrawerForm(next);
    setDrawerFormOriginal(next);
    setPermState({});
    setPermStateOriginal({});
  }
  function handleEditDrawer() { if (selectedDrawerId != null) setDrawerEditing(true); }

  async function handleDeleteDrawer() {
    if (selectedDrawerId == null) return; if (!window.confirm('Delete this drawer?')) return;
    setLoading(true); setError(null);
    try { await deleteDrawer(selectedDrawerId); await init(); setSelectedDrawerId(null); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function handleSaveDrawer(): Promise<number> {
    setLoading(true); setError(null);
    try {
      // Backend expects PascalCase Drawer* fields (see SharedLogic.Models.Drawer + validation in SharedLogic.Repos.Drawers.Verify).
      const payload: any = {
        drawerName: drawerForm.drawerName,
        drawerDescription: drawerForm.drawerDescription,
        drawerOrder: Number(drawerForm.drawerOrder ?? 0),
        drawerPages: (drawerForm.drawerPages ?? '').trim()
      };

      // These are REQUIRED by backend (non-zero).
      // Prefer PascalCase to ensure model binding; keep camelCase aliases for backward compatibility.
      if (drawerForm.folderFieldModelId != null) {
        payload.drawerFolderFieldModelId = drawerForm.folderFieldModelId;
      }
      if (drawerForm.documentFieldModelId != null) {
        payload.drawerDocumentFieldModelId = drawerForm.documentFieldModelId;
      }
      payload.metadataOption = drawerForm.metadataOption;
      let id = selectedDrawerId;
      if (id == null) { const nd = await addDrawer(payload); id = nd.id; }
      else { await updateDrawer(id, payload); }

  await init();
  setSelectedDrawerId(id!);
  // keep edit mode; caller decides whether to close
  return id!;
    } catch (e: any) { setError(e.message); throw e; } finally { setLoading(false); }
  }

  async function handleSaveAll() {
    setLoading(true);
    setError(null);
    try {
      const id = await handleSaveDrawer();
      await savePermissions(id);
      await loadPermissions(id);
      setDrawerFormOriginal(drawerForm);
      // keep the form open after save
      setDrawerEditing(true);
    } catch (e: any) {
      // error is already surfaced via setError
    } finally {
      setLoading(false);
    }
  }

  function handleCancelAll() {
    setDrawerForm(drawerFormOriginal);
    setPermState(permStateOriginal);
    setDrawerEditing(false);
    setError(null);
  }

  function onDrawerFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    if (name === 'folderFieldModelId' || name === 'documentFieldModelId') setDrawerForm(p => ({ ...p, [name]: value ? Number(value) : null } as any));
    else if (name === 'drawerOrder' ) setDrawerForm(p => ({ ...p, drawerOrder: Number(value) }));
    else if (name === 'metadataOption') setDrawerForm(p => ({ ...p, metadataOption: (value === '1' ? 1 : 0) }));
    else setDrawerForm(p => ({ ...p, [name]: value }));
  }

  function ensureGroupState(prev: PermState, groupId: number) { if (!prev[groupId]) prev[groupId] = { read: false, write: false, modify: false, delete: false, full: false }; return prev[groupId]; }
  function togglePerm(groupId: number, key: keyof PermState[number]) {
    setPermState(prev => {
      const clone: PermState = { ...prev };
      const gs = ensureGroupState(clone, Number(groupId));
      (gs as any)[key] = !(gs as any)[key];
      if (key !== 'full' && gs.full) gs.full = false;
      return clone;
    });
  }
  function setFull(groupId: number) {
    setPermState(prev => {
      const clone: PermState = { ...prev };
      const gs = ensureGroupState(clone, Number(groupId));
      const newFull = !gs.full;
      gs.full = newFull;
      gs.read = newFull;
      gs.write = newFull;
      gs.modify = newFull;
      gs.delete = newFull;
      return clone;
    });
  }

  async function ensureTemplate(read: boolean, write: boolean, modify: boolean, del: boolean, full: boolean): Promise<ApiPermissionTemplate> {
    // try find existing
    const found = templates.find(t =>
      Boolean(t.fullControl ?? (t as any).FullControl) === Boolean(full) &&
      Boolean(t.canRead ?? (t as any).CanRead) === Boolean(read) &&
      Boolean(t.canWrite ?? (t as any).CanWrite) === Boolean(write) &&
      Boolean(t.canModify ?? (t as any).CanModify) === Boolean(modify) &&
      Boolean(t.canDelete ?? (t as any).CanDelete) === Boolean(del)
    );
    if (found) return found;
    // create
    const label = full ? 'Full Control' : ['R','W','M','D'].filter((flag, idx) => [read, write, modify, del][idx]).join('') || 'None';
    const tmpl = await createPermissionTemplate({ canRead: !!read, canWrite: !!write, canModify: !!modify, canDelete: !!del, fullControl: !!full, permissionLabel: label });
    setTemplates(prev => [...prev, tmpl]);
    return tmpl;
  }

    async function persistGroupPermission({ groupId, groupState, currentPerms, drawerId }: { groupId: number, groupState: PermState[number], currentPerms: ApiPermission[], drawerId: number }) {
        //console.log('Persisting permission for group', groupId, groupState);
        const hasAny = groupState.full || groupState.read || groupState.write || groupState.modify || groupState.delete;
        const existing = currentPerms.find(p => Number(p.groupId) === groupId && Number(p.objectId) === Number(drawerId) && Number(p.objectTypeId) === OBJECTTYPE_DRAWER);
        //console.log('Existing permission:', existing);
        if (!hasAny && existing) {
            //console.log('Deleting permission', existing.id);
            await deletePermission(existing.id);
            return;
        }
        if (hasAny) {
            const tmpl = await ensureTemplate(groupState.read, groupState.write, groupState.modify, groupState.delete, groupState.full);
            if (!existing) {
                //console.log('Adding permission for group', groupId, 'with template', tmpl.id);
                await addPermission({ groupId, objectId: drawerId, objectTypeId: OBJECTTYPE_DRAWER, permissionTemplateId: tmpl.id });
            } else if (existing.permissionTemplateId !=tmpl.id) {
                //console.log('Updating permission for group', groupId, 'from', existing.permissionTemplateId, 'to template', tmpl.id);
                await deletePermission(existing.id);
                await addPermission({ groupId, objectId: drawerId, objectTypeId: OBJECTTYPE_DRAWER, permissionTemplateId: tmpl.id });
            }
        }
    }

  async function savePermissions(drawerIdOverride?: number) {
    const drawerId = drawerIdOverride ?? selectedDrawerId;
    if (drawerId == null) return;
    setLoading(true); setError(null);
    try {
      const currentPerms = await getDrawerPermissionsByDrawer(drawerId);
      for (const group of sortedGroups) {
        const groupId = Number((group as any).id);
        const groupState = permState[groupId];
        if (!groupState) continue;
        await persistGroupPermission({ groupId, groupState, currentPerms, drawerId });
      }
      await loadPermissions(drawerId);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  const folderModelName = fieldModels.find(fm => fm.id === drawerForm.folderFieldModelId)?.fieldModelName;
  const documentModelName = fieldModels.find(fm => fm.id === drawerForm.documentFieldModelId)?.fieldModelName;

  const folderFieldModels = useMemo(() => {
    const all = Array.isArray(fieldModels) ? fieldModels : [];
    return all.filter((fm: any) => {
      const t = String(fm.fieldModelType ?? fm.FieldModelType ?? '').toLowerCase();
      return t === 'folder';
    });
  }, [fieldModels]);

  const documentFieldModels = useMemo(() => {
    const all = Array.isArray(fieldModels) ? fieldModels : [];
    return all.filter((fm: any) => {
      const t = String(fm.fieldModelType ?? fm.FieldModelType ?? '').toLowerCase();
      return t === 'document';
    });
  }, [fieldModels]);

  const AVAILABLE_PAGES = [
    { key: 'browse', label: 'Browse' },
    { key: 'search', label: 'Search' },
    { key: 'dossier', label: 'Dossier' }
  ];

  // label lookup
  const PAGE_LABEL: Record<string, string> = {
    browse: 'Browse',
    search: 'Search',
    dossier: 'Dossier'
  };

  function parsePages(raw: string): string[] {
    return (raw || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  function setPages(next: string[]) {
    setDrawerForm(p => ({ ...p, drawerPages: next.join(',') }));
    setPagesFlashKey(k => k + 1);
  }

  function togglePage(key: string) {
    const pages = parsePages(drawerForm.drawerPages);
    if (pages.includes(key)) setPages(pages.filter(p => p !== key));
    else setPages([...pages, key]);
  }

  function movePage(key: string, dir: -1 | 1) {
    const pages = parsePages(drawerForm.drawerPages);
    const idx = pages.indexOf(key);
    if (idx === -1) return;
    const target = idx + dir;
    if (target < 0 || target >= pages.length) return;
    const copy = [...pages];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    setPages(copy);
  }

  return (
    <div className="adm-shell">
      <div className="adm-master">
        <div className="adm-master-head">
          <h3>Drawers</h3>
          <button onClick={handleAddDrawer} title='Add drawer' className="adm-add-btn">
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='12' y1='5' x2='12' y2='19' /><line x1='5' y1='12' x2='19' y2='12' /></svg>
          </button>
        </div>
        {loading && <div className="adm-status">Loading...</div>}
        {error && <div className="adm-status adm-status--error">{error}</div>}
        <div className="adm-master-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {sortedDrawers.map(d => {
                const sel = d.id === selectedDrawerId;
                return (
                  <tr key={d.id} onClick={() => handleSelectDrawer(d.id)} className={`adm-row${sel ? ' is-selected' : ''}`}>
                    <td>{d.drawerName}</td>
                  </tr>
                );
              })}
              {drawers.length === 0 && !loading && (<tr><td className="adm-empty">No drawers</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="adm-detail adm-detail--scroll">
        {drawerEditing ? (
          <div style={{ maxWidth: 1220 }}>
            <h3 style={{ marginTop: 0 }}>{selectedDrawerId == null ? 'Add Drawer' : 'Edit Drawer'}</h3>
            <form onSubmit={e => { e.preventDefault(); handleSaveAll(); }} className="adm-card" style={{ display: 'flex', flexDirection: 'column', position: 'relative', maxWidth: 900 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ width: 240, fontWeight: 500 }}>Name</label>
                <input name='drawerName' value={drawerForm.drawerName} onChange={onDrawerFormChange} required className="adm-input" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ width: 240, fontWeight: 500 }}>Description</label>
                <input name='drawerDescription' value={drawerForm.drawerDescription} onChange={onDrawerFormChange} className="adm-input" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ width: 240, fontWeight: 500 }}>Folder Field Model</label>
                <select name='folderFieldModelId' value={drawerForm.folderFieldModelId ?? ''} onChange={onDrawerFormChange} className="adm-input">
                  <option value=''>-- select --</option>
                  {folderFieldModels.map((fm: any) => (
                    <option key={fm.id} value={fm.id}>{fm.fieldModelName ?? fm.FieldModelName}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <label style={{ width: 240, fontWeight: 500 }}>Document Field Model</label>
                <select name='documentFieldModelId' value={drawerForm.documentFieldModelId ?? ''} onChange={onDrawerFormChange} className="adm-input">
                  <option value=''>-- select --</option>
                  {documentFieldModels.map((fm: any) => (
                    <option key={fm.id} value={fm.id}>{fm.fieldModelName ?? fm.FieldModelName}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
                <label style={{ width: 240, fontWeight: 500 }}>Pages (order)</label>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div
                    key={pagesFlashKey}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      animation: 'drawerPagesFlash 220ms ease-out'
                    }}
                  >
                    {(() => {
                      const pages = parsePages(drawerForm.drawerPages);
                      const enabledOrdered = pages.filter(p => PAGE_LABEL[p]);
                      const enabledSet = new Set(enabledOrdered);
                      const disabled = AVAILABLE_PAGES.filter(p => !enabledSet.has(p.key));

                      // Enabled first (in current order), then disabled.
                      const orderedUi = [
                        ...enabledOrdered.map(k => ({ key: k, label: PAGE_LABEL[k] })),
                        ...disabled
                      ];

                      return orderedUi.map(p => {
                        const isOn = enabledSet.has(p.key);
                        const idx = enabledOrdered.indexOf(p.key);
                        return (
                          <div
                            key={p.key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              border: '1px solid var(--sc-border)',
                              borderRadius: 8,
                              padding: '8px 10px',
                              background: isOn ? '#eef6ff' : 'var(--sc-surface-muted)',
                              transition: 'background-color 180ms ease-out'
                            }}
                          >
                            <input type='checkbox' checked={isOn} onChange={() => togglePage(p.key)} />
                            <div style={{ width: 120, fontWeight: 600 }}>{p.label}</div>
                            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                              <button
                                type='button'
                                className="adm-btn adm-btn-secondary"
                                style={{ padding: '6px 10px' }}
                                disabled={!isOn || idx <= 0}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); movePage(p.key, -1); }}
                              >
                                Up
                              </button>
                              <button
                                type='button'
                                className="adm-btn adm-btn-secondary"
                                style={{ padding: '6px 10px' }}
                                disabled={!isOn || idx === -1 || idx >= enabledOrdered.length - 1}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); movePage(p.key, 1); }}
                              >
                                Down
                              </button>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <style>{`\n                    @keyframes drawerPagesFlash {\n                      from { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.40); }\n                      to { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0.0); }\n                    }\n                  `}</style>
                  <div className="adm-hint" style={{ marginTop: 6 }}>
                    Enabled pages appear in the menu for this drawer. The first enabled page opens by default after selecting a drawer.
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
                <label style={{ width: 240, fontWeight: 500 }}>Metadata contains coding</label>
                <select name='metadataOption' value={String(drawerForm.metadataOption)} onChange={onDrawerFormChange} className="adm-input" style={{ flex: 1, minWidth: 220 }}>
                  <option value='0'>No</option>
                  <option value='1'>Yes (coding)</option>
                </select>
                <div className="adm-hint" style={{ flexBasis: '100%', marginLeft: 252, marginTop: 6 }}>
                  When set to “No”, the metadata view option is disabled for documents.
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Permissions per Group</h4>
                <div className="adm-scroll-x">
                  <table className="adm-table adm-table--compact adm-table--auto" style={{ minWidth: 900 }}>
                    <thead>
                      <tr>
                        <th>Group</th>
                        <th className="adm-center">Read</th>
                        <th className="adm-center">Edit</th>
                        <th className="adm-center">Create</th>
                        <th className="adm-center">Delete</th>
                        <th className="adm-center">Full</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGroups.map(g => {
                        const gid = Number(g.id);
                        const st = permState[gid] || { read: false, write: false, modify: false, delete: false, full: false };
                        return (
                          <tr key={gid} className={st.full ? 'is-highlight' : undefined}>
                            <td>{g.groupName}</td>
                            <td className="adm-center"><input type='checkbox' checked={!!st.read} onChange={() => togglePerm(gid, 'read')} disabled={!!st.full} /></td>
                            <td className="adm-center"><input type='checkbox' checked={!!st.modify} onChange={() => togglePerm(gid, 'modify')} disabled={!!st.full} /></td>
                            <td className="adm-center"><input type='checkbox' checked={!!st.write} onChange={() => togglePerm(gid, 'write')} disabled={!!st.full} /></td>
                            <td className="adm-center"><input type='checkbox' checked={!!st.delete} onChange={() => togglePerm(gid, 'delete')} disabled={!!st.full} /></td>
                            <td className="adm-center"><input type='checkbox' checked={!!st.full} onChange={() => setFull(gid)} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="adm-actions adm-actions--spread" style={{ marginTop: 16 }}>
                  <div className="adm-actions-group">
                    {/* Unified save: drawer + permissions */}
                    <button type='submit' className="adm-btn adm-btn-primary" disabled={loading}>Save</button>
                    <button type='button' className="adm-btn adm-btn-secondary" onClick={handleCancelAll} disabled={loading}>Cancel</button>
                  </div>
                  {selectedDrawerId != null && (
                    <button type='button' className="adm-btn adm-btn-danger" onClick={handleDeleteDrawer} disabled={loading}>Delete</button>
                  )}
                </div>

                {/* Permissions are saved together with drawer settings via the main Save button */}
                          </div>
            </form>
          </div>
        ) : (
          <div className="adm-placeholder">
            Select a drawer or click the + icon to add a new drawer.
          </div>
        )}
      </div>
    </div>
  );
}
